import type { FieldJob, FieldJobResult } from "@riea-water/contracts";
import { useRef, useState, type FormEvent } from "react";
import type { ApiClient } from "@riea-water/api-client";
import { useCompleteFieldJob, useUploadEvidence } from "../api/queries.js";
import { ABORT_LABEL, JOB_TYPE_LABEL } from "./field-helpers.js";

const LOCATION_TYPES = [
  ["external_boundary_box", "External boundary box"],
  ["external_chamber", "External chamber"],
  ["internal", "Internal"],
  ["other", "Other"],
] as const;

/**
 * The web completion form (FLD-03): what the job's type needs, a photo, or
 * an abort with its reason. Submitting records the field record and the
 * registry events for the type, links the evidence to the open findings and
 * re-checks them (FLD-04, D-61, D-62).
 */
export function CompleteJobSheet(props: {
  client: ApiClient;
  getToken: () => Promise<string | null>;
  job: FieldJob | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { job } = props;
  const complete = useCompleteFieldJob(props.client);
  const upload = useUploadEvidence(props.getToken);
  const [outcome, setOutcome] = useState<"completed" | "aborted">("completed");
  const [error, setError] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);

  if (!job) return null;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const data = new FormData(event.currentTarget);
    const text = (name: string) => {
      const value = data.get(name);
      return typeof value === "string" ? value.trim() : "";
    };
    const num = (name: string) => (text(name) === "" ? undefined : Number(text(name)));
    try {
      const evidenceIds: string[] = [];
      const photo = data.get("photo");
      if (photo instanceof File && photo.size > 0) {
        const stored = await upload.mutateAsync({
          file: photo,
          kind: "photo",
          attachTo: [{ type: "field_job", id: job.id }],
        });
        evidenceIds.push(stored.id);
      }
      if (outcome === "aborted") {
        await complete.mutateAsync({
          jobId: job.id,
          outcome,
          abortReason: text("abortReason") as keyof typeof ABORT_LABEL,
          evidenceIds,
        });
        props.onDone(`${job.ref} aborted · FieldJobAborted recorded`);
        return;
      }
      const result: FieldJobResult = {};
      const readValue = num("readValue");
      if (readValue !== undefined) result.readValue = readValue;
      if (text("serialObserved")) result.serialObserved = text("serialObserved");
      if (text("locationType")) result.locationType = text("locationType");
      if (text("gridRef")) result.gridRef = text("gridRef");
      if (text("locationDescription")) result.locationDescription = text("locationDescription");
      if (text("accessNotes")) result.accessNotes = text("accessNotes");
      if (text("meterWorking")) result.meterWorking = text("meterWorking") === "yes";
      if (job.type === "survey_premises")
        result.supplyConfirmed = data.get("supplyConfirmed") === "on";
      if (job.type === "exchange") {
        const finalReadOld = num("finalReadOld");
        const initialReadNew = num("initialReadNew");
        result.exchanged = {
          ...(finalReadOld !== undefined ? { finalReadOld } : {}),
          ...(text("newSerial") ? { newSerial: text("newSerial") } : {}),
          ...(initialReadNew !== undefined ? { initialReadNew } : {}),
        };
      }
      await complete.mutateAsync({ jobId: job.id, outcome, result, evidenceIds });
      props.onDone(
        `${job.ref} completed · ${job.type === "exchange" ? "MeterExchanged" : "FieldJobCompleted"} recorded · linked findings re-checked`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const busy = complete.isPending || upload.isPending;
  const field = (label: string, input: JSX.Element) => (
    <label>
      {label}
      {input}
    </label>
  );

  return (
    <form method="dialog" onSubmit={(e) => void onSubmit(e)} ref={form} key={job.id}>
      <h2 id="complete-job-title" style={{ margin: 0 }}>
        Complete {job.ref} · {JOB_TYPE_LABEL[job.type]}
      </h2>
      <div className="muted" style={{ fontSize: ".8rem", whiteSpace: "pre-line" }}>
        {job.brief}
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="eyebrow">Outcome</legend>
        <div className="split">
          <label style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
            <input
              type="radio"
              name="outcome"
              value="completed"
              checked={outcome === "completed"}
              onChange={() => setOutcome("completed")}
            />
            Completed
          </label>
          <label style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
            <input
              type="radio"
              name="outcome"
              value="aborted"
              checked={outcome === "aborted"}
              onChange={() => setOutcome("aborted")}
            />
            Aborted
          </label>
        </div>
      </fieldset>
      {outcome === "aborted" ? (
        field(
          "Reason",
          <select name="abortReason" required defaultValue="no_access">
            {Object.entries(ABORT_LABEL).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>,
        )
      ) : (
        <>
          {job.type === "read"
            ? field(
                "Read value (m³)",
                <input name="readValue" type="number" step="0.001" required />,
              )
            : null}
          {job.type === "locate" ? (
            <>
              {field(
                "Location type",
                <select name="locationType" required defaultValue="">
                  <option value="" disabled>
                    Choose
                  </option>
                  {LOCATION_TYPES.map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>,
              )}
              {field("Grid reference", <input name="gridRef" placeholder="51.78560,-1.48470" />)}
              {field("Description", <input name="locationDescription" />)}
              {field("Access notes", <input name="accessNotes" />)}
            </>
          ) : null}
          {job.type === "verify_serial"
            ? field("Serial observed", <input name="serialObserved" required autoComplete="off" />)
            : null}
          {job.type === "meter_test"
            ? field(
                "Meter working",
                <select name="meterWorking" required defaultValue="">
                  <option value="" disabled>
                    Choose
                  </option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>,
              )
            : null}
          {job.type === "exchange" ? (
            <>
              {field(
                "Final read of the old meter (m³)",
                <input name="finalReadOld" type="number" step="0.001" required />,
              )}
              {field(
                "Serial of the new meter",
                <input name="newSerial" required autoComplete="off" />,
              )}
              {field(
                "Initial read of the new meter (m³)",
                <input name="initialReadNew" type="number" step="0.001" required />,
              )}
            </>
          ) : null}
          {job.type === "verify_serial" || job.type === "meter_test"
            ? field(
                "Read value (m³), if taken",
                <input name="readValue" type="number" step="0.001" />,
              )
            : null}
          {job.type === "survey_premises" ? (
            <label style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
              <input type="checkbox" name="supplyConfirmed" />
              Water supply confirmed at the site
            </label>
          ) : null}
        </>
      )}
      {field("Photo", <input name="photo" type="file" accept="image/jpeg,image/png" />)}
      <div className="faint" style={{ fontSize: ".76rem" }}>
        Completing a job records the field record and the registry events for its type, with the job
        as evidence, and re-checks the findings it touches.
      </div>
      {error ? (
        <div className="pill crit" role="alert" style={{ whiteSpace: "normal" }}>
          {error}
        </div>
      ) : null}
      <div className="split" style={{ justifyContent: "flex-end" }}>
        <button className="btn sm" type="button" onClick={props.onClose}>
          Cancel
        </button>
        <button className="btn sm primary" type="submit" disabled={busy}>
          {outcome === "aborted" ? "Record the abort" : "Record the completion"}
        </button>
      </div>
    </form>
  );
}
